import styled from "styled-components";
import Button, { TButtonIconVariants, TButtonProps } from "../Button";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  UilDollarSign,
  UilEditAlt,
  UilExclamationTriangle,
  UilExport,
  UilImport,
  UilInfoCircle,
  UilLink,
  UilLinkBroken,
  UilSearch,
  UilSync,
} from "@iconscout/react-unicons";
import TableGrid, { TRow, TTableProps, TText } from "../TableGrid";
import AddPayable from "./Forms/AddPayable";
import {
  setIsRestoringQuerystring,
  TInvoice,
  TNexaasProduct,
  TPaymentSlip,
} from "../../../redux/reducers/globalState";
import LinkPayable from "./Forms/LinkPayable";
import InstallmentAccount from "./Forms/InstallmentAccount";
import Goods from "./Forms/Goods";
import AddPaymentSlip from "./Forms/AddPaymentSlip";
import LinkInvoice from "./Forms/LinkInvoice";
import LinkPaymentSlip from "./Forms/LinkPaymentSlip";
import GoodsAutomated from "./Forms/GoodsAutomated";
import GoodsStockCheck from "./Forms/GoodsStockCheck";
import RenameDocument from "./Forms/RenameDocument";
import {
  getQuerystringParams,
  TQuerystringParams,
} from "../../../utils/miscellaneous";
import { useNavigationManager } from "../../../hooks/useNavigationManager";
import AccessKeyImport from "./Forms/AccessKeyImport";
import GoodsCompleted from "./Forms/GoodsCompleted";
import useOutsideClickHandler from "../../../hooks/useOutsideClickHandler";
import { useTypedDispatch } from "../../../hooks/useTypedDispatch";
import ExportIssuedInvoice from "./Forms/ExportIssuedInvoice";
import { TIssuedExportedInvoice } from "../../../pages/IssuedInvoices/Export/mock";

const Container = styled.div<{ $shouldShow: "show" | "hide" }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 99999;

  width: 100vw;
  height: 100vh;

  display: ${({ $shouldShow }) => ($shouldShow === "show" ? `flex` : "none")};
  align-items: center;
  justify-content: center;
`;

const CustomBackdrop = styled.div`
  z-index: 5;
  position: fixed;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  background: var(--color-dark-blue-700);
  opacity: 0.25;
`;

const Dialog = styled.dialog<{
  $shouldShow: "show" | "hide";
  $childVariant: TMainVariants;
  $formVariant?: TModalFormVariants;
  $shouldShowTable?: boolean;
}>`
  z-index: 6;

  padding: 24px;
  background: var(--color-light-0);

  border: 1px solid var(--color-light-200);
  border-radius: 8px;

  max-height: calc(100vh - 50px);
  ${({ $formVariant }) =>
    ($formVariant?.includes("goodsStockCheck") ||
      $formVariant?.includes("goodsCompleted") ||
      $formVariant?.includes("exportIssuedInvoice")) &&
    "height: auto;"}

  ${({ $formVariant, $shouldShowTable }) =>
    !($formVariant === "linkPayable" && !$shouldShowTable) &&
    "overflow-y: auto;"}

  ${({ $childVariant, $formVariant }) =>
    $childVariant === "form"
      ? $formVariant === "goods"
        ? "width: 434px;"
        : $formVariant === "addPayable" || $formVariant === "addPaymentSlip"
        ? "width: 925px;"
        : $formVariant?.includes("link") ||
          $formVariant?.includes("goodsStockCheck") ||
          $formVariant?.includes("goodsCompleted")
        ? "width: 1080px;"
        : $formVariant?.includes("accessKeyImport")
        ? " width: 542px;"
        : $formVariant?.includes("exportIssuedInvoice")
        ? "width: 690px;" + "min-height: 367px;"
        : "width: 660px;"
      : $childVariant === "table" &&
        window.location.pathname.includes("invoices")
      ? "width: 838px;"
      : "width: 660px;"}

  & > h1 {
    font-size: 24px;
    font-family: var(--font-title-bold);
    color: var(--color-text-title);
  }

  & > p {
    font-size: 12px;
    font-family: var(--font-paragraph-default);
    color: var(--color-text-labels);
  }

  & > svg {
    width: 24px;
    height: 24px;
  }
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;

  border-bottom: 1px solid var(--color-light-200);
  padding-bottom: 5px;

  .infoArea {
    display: flex;
    align-items: center;

    & .titleArea {
      display: flex;
      align-items: center;

      & > h1 {
        font-size: 24px;
        font-family: var(--font-title-bold);
        color: var(--color-text-title);
      }

      & > *:first-child {
        margin-right: 5px;
      }

      svg {
        width: 29px;
        height: 29px;
      }
    }
  }

  & > button {
    border: none;
    box-shadow: none;
    padding: 0;

    svg {
      color: var(--color-dark-blue-500);
      width: 24px;
      height: 24px;
    }

    &:hover {
      background: transparent;
    }
  }
`;

const Main = styled.main<{ $hasDescription: boolean }>`
  padding: 0;
  padding-top: ${({ $hasDescription }) => (!$hasDescription ? "24px" : "0px")};

  ${({ $hasDescription }) => $hasDescription && "thead { padding-top: 0px;}"}

  & > p,
  & > b {
    color: var(--color-text-paragraph);
    font-size: 16px;
  }

  p {
    font-family: var(--font-paragraph-default);
  }

  b {
    font-family: var(--font-paragraph-bold);
  }

  & > section {
    margin-top: 0;
  }
`;

const Footer = styled.footer<{ $text: string }>`
  border-top: 1px solid var(--color-light-200);
  padding: 0px;
  padding-top: 16px;

  display: flex;
  justify-content: space-between;

  .ak-textContainer {
    max-width: 253px;
    min-width: 252px;
    border-radius: 8px;
    padding: 8px;
    background: var(--color-light-100);

    font-size: 12px;
    color: var(--color-text-labels);
  }

  .textContainer {
    b,
    p {
      font-size: 14px;
      color: var(--color-text-paragraph);
    }

    b {
      font-family: var(--font-paragraph-bold);
    }

    p {
      font-family: var(--font-paragraph-default);
    }
  }

  .buttonsContainer {
    display: flex;
    justify-content: flex-end;

    ${({ $text }) => $text.length === 0 && "width: 100%;"}

    & button:not(& button:last-child) {
      margin-right: 12px;
    }
  }
`;

const DescriptionContainer = styled.div`
  margin: 16px 0;

  p {
    font-size: 14px;
    font-family: var(--font-paragraph-default);
    color: var(--color-text-paragraph);
  }
`;

type TModalIcons =
  | "exclamationTriangle"
  | "infoCircle"
  | "dolarSign"
  | "link"
  | "breakLink"
  | "import"
  | "export"
  | "edit"
  | "search"
  | "sync";
type TModalIconsColors = "rose" | "blue" | "red" | "orange" | "green";

type TModalIcon = {
  type: TModalIcons;
  color: TModalIconsColors;
};

type TModalFormVariants =
  | "addPayable"
  | "addPaymentSlip"
  | "linkPayable"
  | "installmentAccount"
  | "goods"
  | "goodsAutomated"
  | "goodsStockCheck"
  | "goodsCompleted"
  | "linkInvoice"
  | "linkPaymentSlip"
  | "renameDocument"
  | "accessKeyImport"
  | "exportIssuedInvoice";

type TModalForm<T extends TModalFormVariants> = T extends "addPayable"
  ? {
      preset: "addPayable";
      invoice: TInvoice;
      footer: {
        buttons: TButtonProps<TButtonIconVariants>[];
      };
    }
  : T extends "exportIssuedInvoice"
  ? {
      preset: "exportIssuedInvoice";
      invoice: TIssuedExportedInvoice;
      footer: {
        buttons: TButtonProps<TButtonIconVariants>[];
      };
    }
  : T extends "addPaymentSlip"
  ? {
      preset: "addPaymentSlip";
      paymentSlip: TPaymentSlip;
      footer: {
        buttons: TButtonProps<TButtonIconVariants>[];
      };
    }
  : T extends "linkPayable"
  ? {
      preset: "linkPayable";
      invoice?: TInvoice;
      paymentSlip?: TPaymentSlip;
      footer: {
        buttons: TButtonProps<TButtonIconVariants>[];
      };
    }
  : T extends "linkInvoice"
  ? {
      preset: "linkInvoice";
      footer: {
        text?: TText[];
        buttons: TButtonProps<TButtonIconVariants>[];
      };
    }
  : T extends "linkPaymentSlip"
  ? {
      preset: "linkPaymentSlip";
      footer: {
        text?: TText[];
        buttons: TButtonProps<TButtonIconVariants>[];
      };
    }
  : T extends "installmentAccount"
  ? {
      preset: "installmentAccount";
      invoice?: TInvoice;
      paymentSlip?: TPaymentSlip;
      footer: {
        buttons: TButtonProps<TButtonIconVariants>[];
      };
    }
  : T extends "goods"
  ? {
      preset: "goods";
      invoice: TInvoice;

      footer: {
        buttons: TButtonProps<TButtonIconVariants>[];
      };
    }
  : T extends "goodsAutomated"
  ? {
      preset: "goodsAutomated";
      invoice: TInvoice;

      onCheckStockNeeded?: (data: {
        success: TNexaasProduct[];
        error: TNexaasProduct[];
      }) => void;
      footer: {
        buttons: TButtonProps<TButtonIconVariants>[];
      };
    }
  : T extends "goodsStockCheck"
  ? {
      preset: "goodsStockCheck";
      data: {
        invoiceId: string;
        success: TNexaasProduct[];
        error: TNexaasProduct[];
      };
      footer: {
        buttons: TButtonProps<TButtonIconVariants>[];
      };
    }
  : T extends "goodsCompleted"
  ? {
      preset: "goodsCompleted";
      data: {
        invoiceId: string;
        success: TNexaasProduct[];
        error: TNexaasProduct[];
      };
      footer: {
        buttons: TButtonProps<TButtonIconVariants>[];
      };
    }
  : T extends "renameDocument"
  ? {
      preset: "renameDocument";
      data: TInvoice | TPaymentSlip;
      footer: { buttons: TButtonProps<TButtonIconVariants>[] };
    }
  : {
      preset: "accessKeyImport";
    };

type TMainVariants = "textOnly" | "table" | "form";

type TMain<T extends TMainVariants> = T extends "textOnly"
  ? { style: "textOnly"; content: TText[] }
  : T extends "table"
  ? { style: "table"; content: TTableProps<TRow> }
  : { style: "form"; content: TModalForm<TModalFormVariants> };

type TFooterVariants = "default" | "form";

type TCommonProps = {
  "data-testid"?: string;
};

type TFooter<T extends TFooterVariants> = T extends "default"
  ? {
      style: "default";
      text?: TText[];
      buttons?: TButtonProps<TButtonIconVariants>[];
    } & TCommonProps
  : {
      style: "form";
      text?: never;
      buttons?: never;
    } & TCommonProps;

type TModalProps = {
  title: string | React.ReactNode;
  description?: string;
  icon?: TModalIcon;
  footer?: TFooter<TFooterVariants>;
  main: TMain<TMainVariants>;
  shouldOpen: boolean;
  onClose: () => void;
};

const Modal: React.FC<TModalProps> = ({
  title,
  description,
  icon,
  footer,
  main,
  shouldOpen,
  onClose,
}) => {
  const clearSelectedRef = useRef<(() => void) | null>(null);

  const { goTo, setQuerystring } = useNavigationManager();

  const prevQueryStringsRef = useRef<TQuerystringParams | null>(null);

  const [shouldShowTable, setShouldShowTable] = useState(false);

  const dispatch = useTypedDispatch();

  const handleIcon = () => {
    if (icon) {
      let color;
      switch (icon.color) {
        case "rose":
          color = getComputedStyle(document.documentElement).getPropertyValue(
            "--color-main-rose"
          );
          break;
        case "blue":
          break;
        case "red":
          color = getComputedStyle(document.documentElement).getPropertyValue(
            "--color-aux-danger-700"
          );
          break;
        case "orange":
          color = getComputedStyle(document.documentElement).getPropertyValue(
            "--color-aux-warning-700"
          );
          break;
        case "green":
      }
      switch (icon.type) {
        case "exclamationTriangle":
          return <UilExclamationTriangle color={color} />;
        case "infoCircle":
          return <UilInfoCircle color={color} />;
        case "dolarSign":
          return <UilDollarSign color={color} />;
        case "link":
          return <UilLink color={color} />;
        case "import":
          return <UilImport color={color} />;
        case "export":
          return <UilExport color={color} />;
        case "breakLink":
          return <UilLinkBroken color={color} />;
        case "edit":
          return <UilEditAlt color={color} />;
        case "search":
          return <UilSearch color={color} />;
        case "sync":
          return <UilSync color={color} />;
      }
    }
  };

  const handleMainRender = useCallback(
    (main: TMain<TMainVariants>) => {
      switch (main.style) {
        case "textOnly":
          return (
            <>
              {...main.content.map((content) => handleTextRender(content))}
              <div style={{ height: "24px", width: "1px" }}></div>
            </>
          );
        case "table":
          return <TableGrid {...main.content} />;
        case "form":
          return (
            <>
              {main.content.preset === "addPayable" && (
                <AddPayable
                  invoice={main.content.invoice}
                  footer={main.content.footer}
                />
              )}
              {main.content.preset === "exportIssuedInvoice" && (
                <ExportIssuedInvoice
                  invoice={main.content.invoice}
                  footer={main.content.footer}
                />
              )}
              {main.content.preset === "addPaymentSlip" && (
                <AddPaymentSlip
                  paymentSlip={main.content.paymentSlip}
                  footer={main.content.footer}
                />
              )}
              {main.content.preset === "linkPayable" && (
                <LinkPayable
                  invoice={main.content.invoice}
                  paymentSlip={main.content.paymentSlip}
                  footer={main.content.footer}
                  setShouldShowTable={setShouldShowTable}
                  shouldShowTable={shouldShowTable}
                />
              )}
              {main.content.preset === "linkPaymentSlip" && (
                <LinkPaymentSlip
                  onClear={(clearFunction) => {
                    clearSelectedRef.current = clearFunction;
                  }}
                  footer={main.content.footer}
                />
              )}
              {main.content.preset === "linkInvoice" && (
                <LinkInvoice
                  onClear={(clearFunction) => {
                    clearSelectedRef.current = clearFunction;
                  }}
                  footer={main.content.footer}
                />
              )}
              {main.content.preset === "installmentAccount" && (
                <InstallmentAccount footer={main.content.footer} />
              )}
              {main.content.preset === "goods" && (
                <Goods
                  invoice={main.content.invoice}
                  footer={main.content.footer}
                />
              )}
              {main.content.preset === "goodsAutomated" && (
                <GoodsAutomated
                  onCheckStockNeeded={main.content.onCheckStockNeeded}
                  invoice={main.content.invoice}
                  footer={main.content.footer}
                />
              )}
              {main.content.preset === "goodsStockCheck" && (
                <GoodsStockCheck
                  data={main.content.data}
                  footer={main.content.footer}
                />
              )}
              {main.content.preset === "goodsCompleted" && (
                <GoodsCompleted
                  data={main.content.data}
                  footer={main.content.footer}
                />
              )}
              {main.content.preset === "renameDocument" && (
                <RenameDocument
                  data={main.content.data}
                  footer={main.content.footer}
                />
              )}
              {main.content.preset === "accessKeyImport" && <AccessKeyImport />}
            </>
          );
      }
    },
    [shouldShowTable]
  );

  const handleClose = React.useCallback(() => {
    if (clearSelectedRef.current) {
      clearSelectedRef.current();
    }
    onClose();
  }, [onClose]);

  const handleTextRender = useCallback((content: TText) => {
    switch (content.style) {
      case "bold":
        return (
          <>
            <b style={{ display: "inline" }}>{content.text}</b>
            {content.shouldBreakLine && <br />}
          </>
        );
      case "default":
        return (
          <>
            <p style={{ display: "inline" }}>{content.text}</p>
            {content.shouldBreakLine && <br />}
          </>
        );
      case "faded":
        return (
          <>
            <span>{content.text}</span>
            {content.shouldBreakLine && <br />}
          </>
        );
    }
  }, []);

  useEffect(() => {
    if (
      ("preset" in main.content &&
        !["linkInvoice", "linkPaymentSlip"].includes(main.content.preset)) ||
      !("preset" in main.content)
    ) {
      const queryStrings = { ...getQuerystringParams() };

      prevQueryStringsRef.current = { ...queryStrings };

      if (shouldOpen) {
        queryStrings.modal = true;
        delete queryStrings.start_date;
        delete queryStrings.end_date;
        delete queryStrings.search;

        setQuerystring(queryStrings);
      } else {
        if (prevQueryStringsRef.current) {
          dispatch(setIsRestoringQuerystring(true));
          setQuerystring(prevQueryStringsRef.current);
          goTo(window.location.pathname, prevQueryStringsRef.current);
        }
      }
    }
    return () => {
      if (
        ("preset" in main.content &&
          !["linkInvoice", "linkPaymentSlip"].includes(main.content.preset)) ||
        !("preset" in main.content)
      ) {
        if (prevQueryStringsRef.current) {
          dispatch(setIsRestoringQuerystring(true));
          setQuerystring(prevQueryStringsRef.current);
          goTo(window.location.pathname, prevQueryStringsRef.current);
        }
      }
    };
  }, [shouldOpen]);

  const [isClicked /* setIsClicked */] = React.useState(true); // true when modal is open

  useOutsideClickHandler({
    setIsClicked: (clicked) => {
      if (!clicked) onClose(); // close modal if click is outside
    },
    isClicked,
    shouldBeClickableElementId: "modal-dialog-id", // the id you assign below
  });

  return (
    <Container $shouldShow={shouldOpen ? "show" : "hide"}>
      <CustomBackdrop />
      <Dialog
        id="modal-dialog-id"
        $formVariant={main.style === "form" ? main.content.preset : undefined}
        $childVariant={main.style}
        open={shouldOpen}
        $shouldShow={shouldOpen ? "show" : "hide"}
        $shouldShowTable={shouldShowTable}
      >
        <Header>
          <div className="infoArea">
            <div className="titleArea">
              {icon && handleIcon()}

              {typeof title === "string" ? <h1>{title}</h1> : title}
            </div>
          </div>
          <Button
            icon="onlyIcon"
            iconType="times"
            variant="secondary"
            onClick={handleClose}
          />
        </Header>
        {description && (
          <DescriptionContainer>
            <p>{description}</p>
          </DescriptionContainer>
        )}
        <Main $hasDescription={!!description}>
          {/* shouldOpen &&  */ handleMainRender(main)}
        </Main>
        {footer && (
          <Footer $text={footer.text ? "true" : ""}>
            {"preset" in main.content &&
              main.content.preset === "accessKeyImport" && (
                <div className="ak-textContainer">
                  <p>
                    A nota é solicitada na SEFAZ para ser adicionada ao portal
                  </p>
                </div>
              )}
            {footer.text && (
              <div
                className="textContainer"
                data-testid={footer["data-testid"]}
              >
                {footer.text.map((content, index) => (
                  <React.Fragment key={index}>
                    {handleTextRender(content)}
                  </React.Fragment>
                ))}
              </div>
            )}
            <div className="buttonsContainer">
              {footer.buttons?.map((buttonProps, index) => (
                <Button key={index} {...buttonProps} />
              ))}
            </div>
          </Footer>
        )}
      </Dialog>
    </Container>
  );
};

export default React.memo(Modal);
